import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiPfpMaker = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-pfp-maker',
			name: 'PFP Maker — AI Headshot Generator | Lift',
			description:
				'Create professional PFP headshots instantly with AI. No watermark. Upload selfies, choose styles, and generate business headshots for LinkedIn, social media, or any profiles. Try now!',
			url: 'https://liftapp.ai/ai-pfp-maker',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-pfp-maker#product' },
				{ '@id': 'https://liftapp.ai/ai-pfp-maker#howto' },
				{ '@id': 'https://liftapp.ai/ai-pfp-maker#benefits' },
				{ '@id': 'https://liftapp.ai/ai-pfp-maker#faq' },
				{ '@id': 'https://liftapp.ai/ai-pfp-maker#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-pfp-maker#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-pfp-maker#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-pfp-maker#product',
			name: 'AI PFP Maker Online',
			description:
				'Generate professional headshots and custom PFP instantly with AI. Get studio-quality profile pictures for LinkedIn, Instagram, and business use, no watermarks.',
			url: 'https://liftapp.ai/ai-pfp-maker',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-pfp-maker#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift generates your PFP automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-pfp-maker#benefits',
			name: 'Who can benefit from AI PFP Maker?',
			description:
				"Lift's AI PFP Maker is useful for professionals, creators, and businesses who want professional headshots and custom profile pictures for social media, LinkedIn, business profiles, and personal branding.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Job Seekers & Career Switchers — Create polished LinkedIn headshots and professional profile pictures that boost credibility and first impressions. Generate AI headshots to stand out on job portals and CVs without expensive photo shoots.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Freelancers & Solopreneurs — Build your personal brand with custom PFP and business headshots for websites, pitch decks, and social profiles. Get multiple professional styles in minutes without photographers.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: "Remote Teams — Unify your company's visual identity with consistent, professional headshots for team directories and About Us pages. Scale corporate headshots for distributed teams without scheduling or logistics hassles.",
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Personal Brand Builders — Refresh your social media PFP with aesthetic and professional headshot options for Instagram, YouTube, and online courses. Experiment with different styles while maintaining authentic, business-ready visuals.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Generate PFP for academic profiles, portfolio websites, and professional networking. Create polished headshots for internship applications and student organization pages without studio sessions.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Professional headshots use cases',
				description:
					'Example of how Lift AI PFP Maker creates professional headshots and custom profile pictures for various use cases.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-pfp-maker#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI PFP Maker?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI PFP Maker is a premium online tool that instantly generates professional headshots and custom profile pictures using AI — ideal for LinkedIn, business profiles, social media, and personal branding.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI PFP Maker work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload a few selfies to Lift's AI headshot generator. Our AI automatically creates professional business headshots with multiple styles, backgrounds, and outfits in minutes — no photo shoot or editing experience needed. Generate custom PFP with studio-quality results.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI PFP Maker uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking cutouts without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-pfp-maker#why-lift',
			name: "Why Lift's AI PFP Maker stands out",
			description:
				"Lift's PFP Maker combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Artificial intelligence headshots',
				description:
					"Comparison of Lift's AI PFP Maker benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-pfp-maker#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-pfp-maker#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI PFP Maker',
					item: 'https://liftapp.ai/ai-pfp-maker',
				},
			],
		},
	],
};
